// hooks/useOgpCache.js
import { useCallback } from 'react';

const CACHE_KEY_PREFIX = 'ogp_cache_';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

const useOgpCache = () => {
  const getCacheKey = useCallback((url) => {
    return `${CACHE_KEY_PREFIX}${btoa(url)}`;
  }, []);

  const getCachedData = useCallback((url) => {
    try {
      const cacheKey = getCacheKey(url);
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      if (now - timestamp > CACHE_TTL) {
        sessionStorage.removeItem(cacheKey);
        return null;
      }
      
      return data;
    } catch (error) {
      console.warn('Failed to read OGP cache:', error);
      return null;
    }
  }, [getCacheKey]);

  const setCachedData = useCallback((url, data) => {
    try {
      const cacheKey = getCacheKey(url);
      const cacheEntry = {
        data,
        timestamp: Date.now()
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (error) {
      console.warn('Failed to write OGP cache:', error);
    }
  }, [getCacheKey]);

  const fetchOgpData = useCallback(async (url) => {
    // Check cache first
    const cachedData = getCachedData(url);
    if (cachedData) {
      return cachedData;
    }

    // Fetch from API
    const API_URL = process.env.REACT_APP_API_URL;
    const response = await fetch(`${API_URL}/ogp-preview?url=${encodeURIComponent(url)}`);
    if (!response.ok) {
      throw new Error('Failed to fetch OGP data');
    }
    
    const data = await response.json();
    
    // Cache the result
    setCachedData(url, data);
    
    return data;
  }, [getCachedData, setCachedData]);

  const preloadOgpData = useCallback(async (urls) => {
    const promises = urls.map(async (url) => {
      try {
        const data = await fetchOgpData(url);
        return { url, data, error: null };
      } catch (error) {
        console.error(`Error fetching OGP data for ${url}:`, error);
        return { url, data: null, error: error.message };
      }
    });

    return Promise.all(promises);
  }, [fetchOgpData]);

  return {
    getCachedData,
    setCachedData,
    fetchOgpData,
    preloadOgpData
  };
};

export default useOgpCache;